import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'

import { Modal } from '@/ui/Modal'
import { Button, ButtonGroup, ButtonGroupOption } from '@/ui/Button'
import { SuggestionOption } from '@/ui/SuggestionInputTable'
import { useModal } from '@/components/Modal'
import { useBusStops } from '@/pages/UnfinishedRecords/hooks/useBusStops'
import { useCombinedLogsTableFormStateStore } from '@/modules/CombinedLogs/store'
import { calculateMileage } from '@/modules/CombinedLogs/helpers/calculateMileage'
import { generateLocationStopValue, parseLocationStopValue } from '@/modules/CombinedLogs/helpers/location'
import { Input } from '@/ui/Input'
import { LowBridgeWarning } from './LowBridgeWarning'
import { getSelectedStops } from '@/modules/CombinedLogs/helpers/getSelectedStops'
import { checkIsToStopValid } from '@/modules/CombinedLogs/helpers/checkIsToStopValid'
import { LogLocationType } from '@/types/logs'

import styles from './EditLocationModal.module.scss'

import BusStopRouteInbound from '@/modules/CombinedLogs/assets/bus-stop-route-inbound.svg'
import BusStopRouteInboundFirst from '@/modules/CombinedLogs/assets/bus-stop-route-inbound-first.svg'
import BusStopRouteOutbound from '@/modules/CombinedLogs/assets/bus-stop-route-outbound.svg'
import BusStopRouteOutboundFirst from '@/modules/CombinedLogs/assets/bus-stop-route-outbound-first.svg'
import BusLastStop from '@/modules/CombinedLogs/assets/bus-stop-route-last.svg'
import { Checkbox } from '@/ui/Checkbox'

export type Props = {
    route: string
    direction: 'outbound' | 'inbound'
    isZeroMileage: boolean
    from?: string
    to?: string
    type?: LogLocationType
    callback: (options: {
        from: string
        to: string
        type: LogLocationType
        mileage: string
        isZeroMileage: boolean
    }) => void
}

type StopVariant = 'inbound' | 'outbound'

const stopFilterOptions: ButtonGroupOption[] = [
    {
        label: 'CRT',
        value: false,
        variant: 'outline',
    },
    {
        label: 'All',
        value: true,
        variant: 'outline',
    },
]

export const EditLocationModal = () => {
    const { closeModal, props } = useModal<Props>()
    const fromRef = useRef<HTMLInputElement>(null)
    const toRef = useRef<HTMLInputElement>(null)
    const [from, setFrom] = useState(props?.from || '')
    const [to, setTo] = useState(props?.to || '')
    const [displayAllStops, setDisplayAllStops] = useState(props?.type === LogLocationType.All)
    const [activeElement, setActiveElement] = useState<HTMLElement | null>(null)
    const [isLowBridgeAccepted, setIsLowBridgeAccepted] = useState(false)
    const [isZeroMileage, setIsZeroMileage] = useState(false)
    const { getRouteBusStops } = useBusStops()
    const routeStops = getRouteBusStops(props?.route)

    const oppDirection = props?.direction === 'inbound' ? 'outbound' : 'inbound'

    const { selectedTrips } = useCombinedLogsTableFormStateStore()

    const getStopOptions = useCallback(
        ({ variant, includeAll }: { variant?: StopVariant; includeAll?: boolean }) => {
            if (!routeStops) {
                return []
            }

            const elements = variant ? routeStops[variant] : [...routeStops.inbound, ...routeStops.outbound]

            return elements.reduce<SuggestionOption[]>((acc, stop) => {
                const option = {
                    id: generateLocationStopValue(stop),
                    label: stop.stopName,
                    value: generateLocationStopValue(stop),
                }

                if (includeAll) {
                    return [...acc, option]
                }

                return stop.isCurtailmentStop ? [...acc, option] : acc
            }, [])
        },
        [routeStops]
    )

    const allStops = useMemo(() => getStopOptions({ includeAll: true }), [getStopOptions])
    const getFilteredStops = useCallback(
        (variant: StopVariant) => getStopOptions({ variant, includeAll: displayAllStops }),
        [displayAllStops, getStopOptions]
    )
    const inboundStops = useMemo(() => {
        if (props?.direction === 'outbound') {
            return getFilteredStops('inbound').reverse()
        }
        return getFilteredStops('inbound')
    }, [getFilteredStops, props?.direction])
    const outboundStops = useMemo(() => {
        if (props?.direction === 'inbound') {
            return getFilteredStops('outbound').reverse()
        }
        return getFilteredStops('outbound')
    }, [getFilteredStops, props?.direction])

    const locationFrom = useMemo(() => allStops.find((item) => item.value === from)?.label, [from, allStops])
    const locationTo = useMemo(() => allStops.find((item) => item.value === to)?.label, [to, allStops])
    const selectedStops = useMemo(() => {
        if (!routeStops) {
            return []
        }

        const allStops = [...routeStops.outbound, ...routeStops.inbound]

        return getSelectedStops(allStops, from, to, LogLocationType.All)
    }, [from, to, routeStops, displayAllStops])

    const needToAcceptLowBridge = useMemo(() => {
        if (selectedStops.length < 2) {
            return false
        }

        return selectedStops.some((stop) => stop.isNearLowBridge)
    }, [selectedStops])

    const isDoneButtonDisabled = useMemo(() => {
        const disabled = !to && !from

        if (isZeroMileage) {
            return false
        }

        return needToAcceptLowBridge ? disabled || !isLowBridgeAccepted : disabled
    }, [to, from, needToAcceptLowBridge, isLowBridgeAccepted, isZeroMileage])

    const checkIsStopSelected = (value: string) => {
        const { stopNumber, direction } = parseLocationStopValue(value)
        return (
            stopNumber &&
            direction &&
            !!selectedStops.find((item) => item.stopNumber === stopNumber && item.direction === direction)
        )
    }

    const checkIsCRTStop = (variant: StopVariant, value: string) => {
        if (!routeStops) {
            return false
        }

        return routeStops[variant].find((item) => item.stopNumber === parseLocationStopValue(value).stopNumber)
            ?.isCurtailmentStop
    }

    const handleSubmit = () => {
        if (!props || (!from && !to && !isZeroMileage)) {
            return
        }
        const { stopNumber, direction } = parseLocationStopValue(from)
        const mileage = isZeroMileage ? '' : calculateMileage(selectedStops, selectedTrips, stopNumber, direction)

        props.callback({
            from,
            to,
            type: displayAllStops ? LogLocationType.All : LogLocationType.CRT,
            mileage,
            isZeroMileage,
        })
        closeModal()
    }

    const handleEditFrom = (event: SyntheticEvent) => {
        event.stopPropagation()

        setFrom('')
        setTimeout(() => {
            fromRef.current?.focus()
        }, 10)
    }

    const handleEditTo = (event: SyntheticEvent) => {
        event.stopPropagation()

        setTo('')
        setTimeout(() => {
            toRef.current?.focus()
        }, 10)
    }

    const handleUpdateStop = (value: string) => {
        if (fromRef.current === activeElement) {
            setFrom(value)
            return
        }

        if (toRef.current === activeElement) {
            if (routeStops && checkIsToStopValid([...routeStops.inbound, ...routeStops.outbound], from, value)) {
                setTo(value)
            }
            return
        }

        if (from && to) {
            setFrom(value)
            setTo('')
            return
        }

        if (!from) {
            setFrom(value)
            return
        }

        if (routeStops && checkIsToStopValid([...routeStops.inbound, ...routeStops.outbound], from, value)) {
            setTo(value)
        }
    }

    const handleChangeBusOptions = (displayAll: boolean) => {
        setFrom('')
        setTo('')
        setDisplayAllStops(displayAll as boolean)
    }

    const handleCloseModal = () => {
        if (!needToAcceptLowBridge || isDoneButtonDisabled) {
            closeModal()
            return
        }

        handleSubmit()
    }

    useEffect(() => {
        if (!routeStops || (!props?.from && !props?.to)) {
            return
        }

        const selectedStops = [...routeStops.inbound, ...routeStops.outbound].filter((stop) => {
            const value = generateLocationStopValue(stop)

            return value === props?.from || value === props?.to
        })

        const isAllStops = selectedStops.some((stop) => !stop.isCurtailmentStop)

        setDisplayAllStops(isAllStops)
    }, [routeStops])

    useEffect(() => {
        setIsLowBridgeAccepted(false)
    }, [needToAcceptLowBridge])

    useEffect(() => {
        const handler = () => {
            setTimeout(() => {
                setActiveElement(document.activeElement as HTMLElement)
            }, 500)
        }

        document.addEventListener('focusin', handler)

        handler()

        return () => {
            document.removeEventListener('focusin', handler)
        }
    }, [])

    useEffect(() => {
        setIsZeroMileage(props?.isZeroMileage ?? false)
    }, [props])

    useEffect(() => {
        if (isZeroMileage) {
            setFrom('')
            setTo('')
        }
    }, [isZeroMileage])

    if (!props) {
        return null
    }

    return (
        <Modal className={styles.modal} onClose={handleCloseModal} hideCloseButton>
            <div className={clsx(styles.inputsContainer, { [styles.disabled]: isZeroMileage })}>
                {locationFrom ? (
                    <div className={styles.infoWrapper} onClick={handleEditFrom}>
                        <span className={styles.label}>From: </span>
                        <span className={styles.text}>{locationFrom}</span>
                    </div>
                ) : (
                    <Input
                        ref={fromRef}
                        placeholder="From"
                        onKeyDown={(e) => e.preventDefault()}
                        transparent
                        fullWidth
                        className={styles.input}
                        disabled={isZeroMileage}
                    />
                )}
                {locationTo ? (
                    <div className={styles.infoWrapper} onClick={handleEditTo}>
                        <span className={styles.label}>To: </span>
                        <span className={styles.text}>{locationTo}</span>
                    </div>
                ) : (
                    <Input
                        ref={toRef}
                        placeholder="To"
                        onKeyDown={(e) => e.preventDefault()}
                        transparent
                        fullWidth
                        className={styles.input}
                        disabled={isZeroMileage}
                    />
                )}
            </div>
            <div className={styles.noMileageContainer}>
                <Checkbox checked={isZeroMileage} onChange={setIsZeroMileage} label={'No mileage lost'} />
            </div>
            <div className={styles.container}>
                <p className={styles.title}>Route {props.route}</p>
                <div className={clsx({ [styles.disabled]: isZeroMileage })}>
                    <ButtonGroup
                        value={displayAllStops}
                        options={stopFilterOptions}
                        onSelect={(displayAll) => {
                            handleChangeBusOptions(displayAll as boolean)
                        }}
                    />
                </div>
            </div>
            <div className={clsx(styles.stopsContainer, { [styles.disabled]: isZeroMileage })}>
                <div className={clsx(styles.stops, styles.outboundStops)}>
                    {(props?.direction === 'outbound' ? outboundStops : inboundStops).map((option, index, array) => (
                        <div
                            key={option.id}
                            className={clsx(styles.stop, styles.outboundStop, {
                                [styles.lastStop]: array.length - 1 === index,
                            })}
                            onClick={() => handleUpdateStop(option.value)}
                        >
                            <p
                                className={clsx(styles.stopName, styles.outboundStopName, {
                                    [styles.stopSelected]: checkIsStopSelected(option.value),
                                })}
                            >
                                {option.label}
                            </p>
                            <span
                                className={clsx(styles.stopIcon, {
                                    [styles.curtailmentStop]: checkIsCRTStop(props?.direction, option.value),
                                })}
                            >
                                {!index && <BusStopRouteInboundFirst />}
                                {index === array.length - 1 && <BusLastStop />}
                                {!!index && index < array.length - 1 && <BusStopRouteInbound />}
                            </span>
                        </div>
                    ))}
                </div>
                <div className={styles.stops}>
                    {(props?.direction === 'outbound' ? inboundStops : outboundStops).map((option, index, array) => (
                        <div
                            key={option.id}
                            className={clsx(styles.stop, { [styles.lastStop]: array.length - 1 === index })}
                            onClick={() => handleUpdateStop(option.value)}
                        >
                            <span
                                className={clsx(styles.stopIcon, {
                                    [styles.curtailmentStop]: checkIsCRTStop(oppDirection, option.value),
                                })}
                            >
                                {!index && <BusStopRouteOutboundFirst />}
                                {index === array.length - 1 && <BusLastStop />}
                                {!!index && index < array.length - 1 && <BusStopRouteOutbound />}
                            </span>
                            <p
                                className={clsx(styles.stopName, styles.inboundStopName, {
                                    [styles.stopSelected]: checkIsStopSelected(option.value),
                                })}
                            >
                                {option.label}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.footer}>
                {needToAcceptLowBridge && !isZeroMileage && <LowBridgeWarning onChange={setIsLowBridgeAccepted} />}
                <div className={styles.actions}>
                    <Button rounded variant="tertiary" onClick={closeModal}>
                        Cancel
                    </Button>
                    <Button rounded variant="secondary" disabled={isDoneButtonDisabled} onClick={handleSubmit}>
                        Done
                    </Button>
                </div>
            </div>
        </Modal>
    )
}
